import { create } from "zustand";
import { enqueueSnackbar } from "notistack";
import apiVotingClient from "../api/api.voting";

export interface QuestionDTO {
    id: number;
    meeting_id: number;
    number: number;
    text: string;
}

export interface MaterialDTO {
    document_id: number | null;
    filename: string;
}

export interface MeetingDTO {
    id: number;
    title: string;
    description?: string;
    form: string;
    starts_at: string;
    ends_at: string;
    status: string;
    decision_threshold: string;
    quorum_threshold: number;
    materials?: MaterialDTO[];
    created_at: string;
    questions: QuestionDTO[];
    service_organization_ids: number[];
    has_voted?: boolean;
}

export interface VoteChoiceItem {
    question_id: number;
    choice: "for" | "against";
}

interface VotingStore {
    meetings: MeetingDTO[];
    loading: boolean;
    getMeetings: () => Promise<MeetingDTO[]>;
    getBallot: (meetingId: number) => Promise<MeetingDTO | null>;
    voteInit: (meetingId: number) => Promise<boolean>;
    voteConfirm: (meetingId: number, code: string, choices: VoteChoiceItem[]) => Promise<boolean>;
    downloadMyBallot: (meetingId: number) => Promise<void>;
    openMaterial: (documentId: number) => Promise<void>;
}

export const useVotingStore = create<VotingStore>((set, get) => ({
    meetings: [],
    loading: false,

    async getMeetings() {
        set({ loading: true });
        try {
            const { data } = await apiVotingClient.get("/api/v1/voting/meetings");
            set({ meetings: data, loading: false });
            return data;
        } catch (error: any) {
            set({ loading: false });
            enqueueSnackbar(error?.response?.data?.detail || "Ошибка при получении списка собраний", {
                variant: "error",
            });
            return [];
        }
    },

    async getBallot(meetingId: number) {
        try {
            const { data } = await apiVotingClient.get(`/api/v1/voting/meetings/${meetingId}/ballot`);
            return data;
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.detail || "Ошибка при загрузке бюллетеня", {
                variant: "error",
            });
            return null;
        }
    },

    async voteInit(meetingId: number) {
        try {
            await apiVotingClient.post("/api/v1/voting/vote/init", { meeting_id: meetingId });
            enqueueSnackbar("Код подтверждения отправлен на ваш Email", {
                variant: "info",
            });
            return true;
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.detail || "Ошибка при запросе кода подтверждения", {
                variant: "error",
            });
            return false;
        }
    },

    async voteConfirm(meetingId: number, code: string, choices: VoteChoiceItem[]) {
        try {
            await apiVotingClient.post("/api/v1/voting/vote/confirm", {
                meeting_id: meetingId,
                code: parseInt(code, 10),
                choices,
            });
            enqueueSnackbar("Голос успешно зафиксирован! Именной PDF-бюллетень отправлен на Ваш Email", { variant: "success" });
            get().getMeetings();
            return true;
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.detail || "Ошибка подтверждения голосования", {
                variant: "error",
            });
            return false;
        }
    },
    downloadMyBallot: async (meetingId: number) => {
        try {
            const res = await apiVotingClient.get<{ url: string }>(`/api/v1/voting/meetings/${meetingId}/my-ballot`);
            if (res.data && res.data.url) {
                const fileUrl = res.data.url.startsWith('http') ? res.data.url : `http://localhost:8821${res.data.url}`;
                const link = document.createElement('a');
                link.href = fileUrl;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.setAttribute('download', `ballot_meeting_${meetingId}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (e: any) {
            enqueueSnackbar("Не удалось скачать файл бюллетеня", { variant: "error" });
        }
    },
    openMaterial: async (documentId: number) => {
        try {
            const { data } = await apiVotingClient.get<{ url: string }>(
                `/api/v1/voting/meetings/materials/download`,
                { params: { document_id: documentId } },
            );
            if (data && data.url) {
                window.open(data.url, "_blank", "noopener,noreferrer");
            }
        } catch (e: any) {
            enqueueSnackbar("Не удалось открыть материал", { variant: "error" });
        }
    },
}));
